import { useCallback } from 'react';

import { type TrackFavoritedBoardParams } from '@air/analytics';
import { type Board } from '@air/api/types';
import { useToasts } from '@air/provider-toast';

import { getFavoritedMessage } from '~/constants/messages';
import { useFavoriteBoards } from '~/swr-hooks/boards/useFavoriteBoards';
import { useUnfavoriteBoards } from '~/swr-hooks/boards/useUnfavoriteBoards';
import { useUpdateBoardFavoriteStatusInAllCaches } from '~/swr-hooks/boards/useUpdateBoardFavoriteStatusInAllCaches';
import { useFollowBoard } from '~/swr-hooks/followedBoards/useFollowBoard';

/**
 * This hook gives you a function to pass clips to that will toggle their current FavoritedBoard
 * state. If any of them are currently unfavorited, it will favorite all of them. If all
 * of them are currently FavoritedBoard, it will unfavorite all of them.
 */
export const useToggleFavoritedBoard = () => {
  const { showToast } = useToasts();
  const { favoriteBoards } = useFavoriteBoards();
  const { unfavoriteBoards } = useUnfavoriteBoards();
  const {
    followBoard: { isPending, mutate },
  } = useFollowBoard();
  const { updateBoardFavoriteStatusInAllCaches } = useUpdateBoardFavoriteStatusInAllCaches();

  const toggleFavoritedBoard = useCallback<
    (args: { board: Board; trackLocation: TrackFavoritedBoardParams['location'] }) => void
  >(
    async ({ board, trackLocation }) => {
      const successful = !board.hasCurrentUser
        ? await favoriteBoards({
            boards: [board],
            trackLocation,
          })
        : await unfavoriteBoards({
            boards: [board],
            trackLocation,
          });

      if (successful) {
        updateBoardFavoriteStatusInAllCaches({ board, newHasCurrentUser: !board.hasCurrentUser });

        showToast(getFavoritedMessage(!board.hasCurrentUser, undefined, board.title), {
          timeBeforeAutoDismiss: 2000,
          suffix: !board.hasCurrentUser && (
            <button
              aria-busy={isPending}
              aria-disabled={isPending}
              className="shrink-0 font-semibold text-14 text-white hover:underline aria-disabled:opacity-50"
              type="button"
              // biome-ignore lint/performance/noJsxPropsBind: the handler closes over this toast's `board`, so there is no stable reference to hoist
              onClick={() => {
                if (isPending) {
                  return;
                }

                mutate(
                  { boardId: board.id, includeDescendants: false },
                  {
                    onSuccess: () => {
                      showToast(`You will receive notifications about changes in ${board.title}`);
                    },
                  },
                );
              }}
            >
              Follow
            </button>
          ),
        });
      }
    },
    [favoriteBoards, isPending, mutate, showToast, unfavoriteBoards, updateBoardFavoriteStatusInAllCaches],
  );

  return {
    toggleFavoritedBoard,
  };
};
